import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../api/auth/AuthContext';
import { getSkuEntitlements } from '../../api/price-list/entitlements-api';
import { SkuEntitlementsContext, SkuEntitlementsLoadedContext, SkuEntitlements } from './PriceListEntitlements';
import { buildAdminRoutes } from './adminRoutes';
import './Admin.css';

const ROUTE_LABELS: Record<string, string> = {
  '/admin': 'Inicio',
  '/admin/lista-de-precios': 'Lista de Precios',
  '/admin/permisos': 'Permisos',
  '/admin/diagnostico': 'Diagnóstico',
};

const IconLock = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const IconChevronLeft = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IconChevronRight = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const Admin: React.FC = () => {
  const { roles } = useAuth();
  const isAdmin = roles.includes('Admin');
  const [entitlements, setEntitlements] = useState<SkuEntitlements>({});
  const [entitlementsLoaded, setEntitlementsLoaded] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('admin-sidebar-collapsed') === 'true'
  );
  const location = useLocation();

  useEffect(() => {
    getSkuEntitlements({
      onSuccess: (data) => { setEntitlements(data); setEntitlementsLoaded(true); },
      onError: () => setEntitlementsLoaded(true),
    });
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('admin-sidebar-collapsed', String(next));
      return next;
    });
  };

  const routes = buildAdminRoutes({ entitlements, entitlementsLoaded, isAdmin });

  const currentLabel = ROUTE_LABELS[location.pathname];
  const isHome = location.pathname === '/admin';

  const sidebarClass = [
    'admin-sidebar',
    sidebarOpen ? 'admin-sidebar--open' : '',
    collapsed ? 'admin-sidebar--collapsed' : '',
  ].filter(Boolean).join(' ');

  return (
    <SkuEntitlementsContext.Provider value={entitlements}>
    <SkuEntitlementsLoadedContext.Provider value={entitlementsLoaded}>
    <div className="admin-layout">

      {sidebarOpen && (
        <div className="admin-overlay" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      <aside className={sidebarClass}>
        <div className="admin-sidebar-brand">
          <span className="admin-sidebar-brand-abbr">ABC</span>
          <span className="admin-sidebar-brand-wordmark">Empleados</span>
          <button
            className="admin-sidebar-brand-toggle"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            {collapsed ? <IconChevronRight /> : <IconChevronLeft />}
          </button>
        </div>
        <div className="admin-sidebar-header">
          <button
            className="admin-sidebar-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Cerrar menú"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <nav>
          {routes.filter(r => r.visible).map(route => (
            <NavLink
              key={route.path}
              to={route.path}
              className={({ isActive }) => isActive ? 'active' : ''}
              title={collapsed ? route.label : undefined}
            >
              <route.Icon size={18} />
              <span className="admin-nav-label">{route.label}</span>
              {route.adminOnly && (
                <span className="admin-nav-admin-icon" aria-label="Admin">
                  <IconLock />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

      </aside>

      <div className="admin-content">
        {!isHome && <div className="admin-topbar">
          <button
            className="admin-menu-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menú de navegación"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <nav className="admin-breadcrumb" aria-label="Ruta de navegación">
            <NavLink to="/admin" className="admin-breadcrumb-link">Portal</NavLink>
            {currentLabel && !isHome && (
              <>
                <span className="admin-breadcrumb-sep" aria-hidden="true">/</span>
                <span className="admin-breadcrumb-current">{currentLabel}</span>
              </>
            )}
          </nav>
        </div>}
        <Outlet />
      </div>

    </div>
    </SkuEntitlementsLoadedContext.Provider>
    </SkuEntitlementsContext.Provider>
  );
};

export default Admin;
